'use client';

import React, { useCallback, useMemo } from 'react';
import { View } from '../../../../utils/react-native-stubs';
import { CouponList } from '../../components';
import { KidsLoading, removeUnavailableItems } from '../../../../utils';
import { CouponAction } from '../../components/coupon-action/coupon-action';
import type { SalesRule } from '../../../../types/front.graphql';
import type { UnifiedSalesRule } from '../../types/sales-rule.types';
import { withCouponProvider } from '../../stores/coupon/withCouponProvider';
import styles from './coupon-screen.module.css';
import type { CouponScreenProps } from './coupon-screen.types';
import { cn, Icon, Text } from '@kidslabs/mario-nextjs-react-ui-core';
import { useCouponContext } from '../../contexts/coupon.context';
import { useSalesRuleList } from '../../hooks/use-sales-rule-list';
import type { DisplayLocation } from '../../../../types/marketing.graphql';
import { useTranslation } from 'next-i18next';

export const CouponScreenContent: React.FC<CouponScreenProps> = ({ onClose, variant = 'mini', isSelectionEmpty }) => {
    const { t } = useTranslation('flash-sale');
    const couponContext = useCouponContext();
    const containerClasses = useMemo(
        () => cn(variant === 'mini' && styles.sheet, variant === 'full' && styles.modal),
        [variant]
    );

    const {
        onSelectCoupon,
        availableCoupon,
        appliedCoupons,
        appliedVouchers,
        loadingAvailableCoupons,
        loadingAppliedCoupons,
        validatingCoupons,
        salesRuleSections,
        onRefresh,
    } = useSalesRuleList();

    const filteredCoupons = useMemo(
        () => availableCoupon?.availableCoupons?.filter((item): item is SalesRule => !!item) ?? [],
        [availableCoupon]
    );

    /**
     * Handle coupon/voucher selection.
     * When a voucher is selected (code starts with __voucher__),
     * store the allocation ID in context so the apply handler can use it.
     */
    const handleSelectItem = useCallback(
        (salesRule: UnifiedSalesRule) => {
            const allocationId = salesRule?.id;
            if (allocationId) {
                // Track that this selection is a voucher
                couponContext?.couponState$?.selectedVoucherInfo?.set({
                    allocationId,
                    voucherId: appliedVouchers.find(v => v.voucher_id)?.voucher_id,
                });
            } else {
                // Clear voucher info when a regular coupon is selected
                couponContext?.couponState$?.selectedVoucherInfo?.set(undefined);
            }
            onSelectCoupon(salesRule);
        },
        [onSelectCoupon, couponContext, appliedVouchers]
    );

    if (loadingAvailableCoupons || loadingAppliedCoupons || validatingCoupons) {
        return (
            <div className={styles.loading}>
                <KidsLoading />
            </div>
        );
    }
    return (
        <View className={containerClasses}>
            {isSelectionEmpty && (
                <View className={styles.selectionEmptyWarning}>
                    <Icon name="info" containerClassName={styles.selectionEmptyWarningIcon} />
                    <Text size="small" className={styles.selectionEmptyWarningText}>
                        {t('text_coupon_selection_empty_warning')}
                    </Text>
                </View>
            )}
            <View className={styles.inputContainer}>
                <CouponAction
                    appliedCoupons={removeUnavailableItems(appliedCoupons)}
                    appliedVouchers={appliedVouchers}
                    coupons={filteredCoupons}
                    variant="header"
                    onRefresh={onRefresh}
                    onSuccess={onClose}
                    isDisabled={isSelectionEmpty}
                />
            </View>
            <View className={styles.listContainer}>
                <CouponList
                    coupons={salesRuleSections}
                    onSelectCoupon={handleSelectItem}
                    allDisabled={isSelectionEmpty}
                />
            </View>
            <View className={styles.buttonContainer}>
                <CouponAction
                    appliedCoupons={removeUnavailableItems(appliedCoupons)}
                    appliedVouchers={appliedVouchers}
                    coupons={filteredCoupons}
                    onSuccess={onClose}
                    onRefresh={onRefresh}
                    isDisabled={isSelectionEmpty}
                />
            </View>
        </View>
    );
};
export const CouponScreen: React.FC<CouponScreenProps & { displayLocation?: DisplayLocation }> =
    withCouponProvider(CouponScreenContent);
